package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.data.dto.UserCardDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.widgets.GradientBorderBox
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

/**
 * Confirm-purchase dialog for a card-shop template. Renders the card preview,
 * its price, and Cancel / Buy now actions. Pass `card = null` to keep it
 * dismissed. The caller is responsible for driving the [purchasing] flag and
 * surfacing [purchaseError] strings from the repo.
 */
@Composable
fun PurchaseCardModal(
    card: UserCardDto?,
    sportName: String,
    purchasing: Boolean,
    purchaseError: String?,
    onConfirm: (UserCardDto) -> Unit,
    onDismiss: () -> Unit,
) {
    if (card == null) return
    val t = LocalAppStrings.current

    // Non-default cards (price > 0) require an active Access subscription.
    // Subscription state is cached app-wide; first open kicks off a fetch if it
    // hasn't loaded yet.
    val container = LocalAppContainer.current
    val isSubscribed by container.isSubscribed.collectAsState()
    LaunchedEffect(card.id) { container.ensureSubscriptionLoaded() }

    // Not subscribed → open the paywall (mobile sheet / desktop popup).
    var paywallOpen by remember(card.id) { mutableStateOf(false) }

    // The card requires a subscription unless it's a default card (matches the
    // backend gate, which keys on isDefault rather than price).
    val requiresSubscription = !card.isDefault

    // Subscription state may not be loaded yet when Buy is tapped, so the backend
    // can still reject the purchase with a 402 PaymentRequired. Catch that and
    // open the paywall instead of just showing the error.
    LaunchedEffect(purchaseError) {
        if (purchaseError != null && isPaymentRequiredError(purchaseError)) {
            paywallOpen = true
        }
    }

    val body: @Composable () -> Unit = {
        Column(
            modifier = Modifier.fillMaxWidth().sGap(20),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            SpanText(
                t.purchaseCardTitle,
                modifier = Modifier
                    .sFontSize(20)
                    .fontWeight(AppTypography.SemiBold)
                    .sLineHeight(26)
                    .color(com.arena_web.ui.theme.ThemeVars.Text2),
            )

            Box(contentAlignment = Alignment.Center) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Medium,
                    cardFrameUrl = card.cardImageUrl,
                    cardAssetsBasePath = card.assetsBasePath,
                    cardTextColor = card.textColor,
                    hideRating = true,
                )
            }

            if (purchaseError != null) {
                SpanText(
                    purchaseError,
                    modifier = Modifier
                        .fillMaxWidth()
                        .sFontSize(14)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(22)
                        .color(com.arena_web.ui.theme.ThemeVars.TextMuted),
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                ActionButton(
                    label = t.commonCancel,
                    primary = false,
                    enabled = !purchasing,
                    onClick = onDismiss,
                    modifier = Modifier.flexGrow(1),
                )
                ActionButton(
                    label = if (purchasing) t.purchaseCardBuying else t.purchaseCardBuyNow,
                    primary = true,
                    enabled = !purchasing,
                    // Gated behind Access → surface a PRO+ badge instead of any
                    // price/cost indicator; tapping opens the paywall.
                    showProPlus = requiresSubscription && !isSubscribed,
                    onClick = {
                        if (!requiresSubscription || isSubscribed) onConfirm(card)
                        else paywallOpen = true
                    },
                    modifier = Modifier.flexGrow(1),
                )
            }
        }
    }

    // Mobile → bottom sheet; desktop → centered modal.
    if (isMobile()) {
        BottomSheet(onDismiss = onDismiss, contentGap = 20) {
            body()
        }
    } else {
        Modal(
            isOpen = true,
            onClose = onDismiss,
            closeOnOverlayClick = !purchasing,
            title = null,
        ) {
            body()
        }
    }

    SubscribeModal(
        isOpen = paywallOpen,
        onClose = { paywallOpen = false },
        zIndex = 1020,
        source = "card_shop",
    )
}

@Composable
private fun ActionButton(
    label: String,
    primary: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    showProPlus: Boolean = false,
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    val interactionModifier = modifier
        .sHeight(46)
        .then(
            if (enabled) Modifier.cursor(Cursor.Pointer).onClick { onClick() }
            else Modifier.styleModifier { property("opacity", "0.6") },
        )
    val labelText: @Composable () -> Unit = {
        Row(
            modifier = Modifier.sGap(8),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(if (primary) org.jetbrains.compose.web.css.Color(sportColors.onPrimary.toString()) else com.arena_web.ui.theme.ThemeVars.Text2),
            )
            if (showProPlus) ProPlusPill(onPrimary = primary)
        }
    }
    if (primary) {
        Box(
            modifier = interactionModifier
                .sBorderRadius(100)
                .backgroundColor(sportColors.primary),
            contentAlignment = Alignment.Center,
        ) { labelText() }
    } else {
        GradientBorderBox(
            modifier = interactionModifier,
            borderRadius = 100,
            background = AppGradients.InteractiveSecondary,
        ) { labelText() }
    }
}

/**
 * Small "PRO+" pill shown on a gated action button when the user isn't
 * subscribed — replaces any price/cost indicator. Tapping the parent button
 * opens the paywall. `onPrimary` picks a treatment legible on a sport-tinted
 * (primary) vs. a translucent secondary button.
 */
@Composable
internal fun ProPlusPill(onPrimary: Boolean) {
    val t = LocalAppStrings.current
    Box(
        modifier = Modifier
            .sBorderRadius(100)
            .sPadding(2, 8)
            .styleModifier {
                property("position", "relative")
                // Translucent fill + the project gradient border via the ring
                // overlay below (a plain `border` can't be a gradient over a
                // translucent fill).
                if (onPrimary) {
                    property("background", "rgba(255, 255, 255, 0.22)")
                } else {
                    property("background", "rgba(11, 22, 46, 0.72)")
                }
            },
        contentAlignment = Alignment.Center,
    ) {
        com.arena_web.ui.sections.profile.edit.GradientBorderRing(radiusPx = 100)
        SpanText(
            t.cardProPlus,
            modifier = Modifier
                .sFontSize(11)
                .fontWeight(AppTypography.SemiBold)
                .sLineHeight(13)
                .sLetterSpacing(0.02)
                .color(com.arena_web.ui.theme.ThemeVars.Text1),
        )
    }
}
